// import CryptoJS from 'crypto-js';
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';
import {
    CompleteRegisterCredentialRequest,
    CheckChallengeRequest,
} from 'remotes/@api/authorization_v4';
import {
    BiometricType,
    checkMobileBiometricType,
} from './checkMobileBiometricType';
import {
    PublicKeyExtendedCredentialAuthorization,
    PublicKeyExtendedCredentialRegister,
} from './interfaces';
import base64url from './utils/base64url-arraybuffer';
import { publicKeyCredentialToJSON } from './utils/utils';
import { AuthorizationService } from 'service/Authorization/AuthorizationService';
import { InfoMethodResponse } from 'service/Authorization/infoMethod';
import { BiometricService } from 'service/Biometric/BiometricService';
import {
    getCanBiometricAuth,
    getNeedBiometric,
    isServiceResponse,
    LocalStorageKeys,
    setCanBiometricAuth,
    setNeedBiometric,
} from 'utils/utils';

interface InternalError {
    code: string | 'userAbortedBiometricGet' | 'userAbortedBiometricCreate';
    message: string | 'userAbortedBiometricGet' | 'userAbortedBiometricCreate';
}

/**
 * WebAuthnStore.
 */
export class WebAuthnStore {
    /**
     * Special field. If it is true, then all fields in store initialized.
     */
    @observable
    isInit = false;

    @observable
    public canBiometricAuth!: boolean;

    @observable
    public needBiometric!: boolean;

    @observable
    public biometricRegistered!: boolean | null;

    @observable
    public readonly biometricType!: keyof typeof BiometricType;

    @observable
    private isLoadingProcess: boolean;

    @observable
    private isCheckIsUserBiometricRegistrationLoadingProcess: boolean;

    @observable
    private isHasUserBiometricRegistration: boolean | null;

    @observable
    private internalError: InternalError | null;

    /**
     * Constructor.
     */
    constructor() {
        this.biometricType = checkMobileBiometricType();
        this.isLoadingProcess = false;
        this.isCheckIsUserBiometricRegistrationLoadingProcess = false;
        this.isHasUserBiometricRegistration = null;
        this.internalError = null;
        this.biometricRegistered = null;
        this.init();
        makeObservable(this);
    }

    /**
     * @description IsLoading.
     * @returns {boolean} Value.
     */
    @computed
    public get isLoading() {
        return this.isLoadingProcess;
    }

    /**
     * @description IsCheckIsUserBiometricRegistrationLoading.
     * @returns {boolean} Value.
     */
    @computed
    public get isCheckIsUserBiometricRegistrationLoading() {
        return this.isCheckIsUserBiometricRegistrationLoadingProcess;
    }

    /**
     * @description BiometricTypeText.
     * @returns {string} BiometricTypeText.
     */
    @computed
    public get biometricTypeText() {
        return this.biometricType === 'faceId' ? 'Face ID' : 'Touch ID';
    }

    /**
     * @description Error.
     * @returns {InternalError | null} Error.
     */
    @computed
    public get error(): InternalError | null {
        return this.internalError;
    }

    /**
     * @description IsHasUserBiometric.
     * @returns {boolean} IsHasUserBiometric.
     */
    @computed
    public get isHasUserBiometric(): boolean | null {
        return this.isHasUserBiometricRegistration;
    }

    /**
     * @description SetIsHasUserBiometricRegistration.
     * @param {boolean} isHasUserBiometricRegistration - IsHasUserBiometricRegistration.
     */
    @action.bound
    public setIsHasUserBiometricRegistration(
        isHasUserBiometricRegistration: boolean,
    ) {
        this.isHasUserBiometricRegistration = isHasUserBiometricRegistration;
    }

    /**
     * @description GetIsUserBiometricRegistration.
     */
    @action.bound
    public async checkIsUserBiometricRegistration() {
        if (this.isHasUserBiometricRegistration === null) {
            try {
                this.isCheckIsUserBiometricRegistrationLoadingProcess = true;
                const infoData = await this.info();
                if (infoData) {
                    this.isHasUserBiometricRegistration =
                        infoData.isHasBiometricRegistration;
                }
            } finally {
                this.isCheckIsUserBiometricRegistrationLoadingProcess = false;
            }
        }
    }

    /**
     * @description CheckUserBiometric.
     * @returns {Promise<Credential | null>} Credential.
     */
    @action.bound
    public async checkUserBiometric(): Promise<CheckChallengeRequest | null> {
        try {
            let response: CheckChallengeRequest | null = null;
            this.internalError = null;
            this.setNeedBiometric(true);
            this.isLoadingProcess = true;
            const challengeData = await this.getChallenge();

            if (challengeData) {
                const publicKeyCredentialRequestOptions: PublicKeyCredentialRequestOptions =
                    {
                        allowCredentials: [
                            {
                                id: base64url.decode(
                                    challengeData.credentialId,
                                ),
                                type: 'public-key',
                            },
                        ],
                        challenge: Uint8Array.from(
                            challengeData.challenge,
                            (c) => c.charCodeAt(0),
                        ),
                        timeout: challengeData.timeout,
                    };

                const credential = (await navigator.credentials.get({
                    publicKey: publicKeyCredentialRequestOptions,
                })) as PublicKeyExtendedCredentialAuthorization | null;
                if (credential) {
                    const formattedData = publicKeyCredentialToJSON(credential);
                    response = {
                        clientDataJSON: formattedData.response.clientDataJSON,
                        signature: formattedData.response.signature,
                        authenticatorData:
                            formattedData.response.authenticatorData,
                    };
                }
            }
            this.isLoadingProcess = false;
            return response;
        } catch (e) {
            if (
                isError(e) &&
                e?.message?.match(
                    /operation either timed out or was not allowed/g,
                )
            ) {
                this.internalError = {
                    code: 'userAbortedBiometricGet',
                    message: 'userAbortedBiometricGet',
                };
                this.setNeedBiometric(false);
            } else {
                this.internalError = {
                    code: 'error',
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    message: e?.message,
                };
            }
            this.isLoadingProcess = false;
            return null;
        } finally {
            this.isLoadingProcess = false;
        }
    }

    /**
     * @description RegisterBiometric.
     * @returns {Promise<Credential | null>} Credential.
     */
    @action.bound
    public async registerBiometric(): Promise<void> {
        try {
            this.isLoadingProcess = true;
            this.internalError = null;
            const publicKey = await this.initial();

            if (publicKey) {
                const credential = await navigator.credentials.create({
                    publicKey,
                });
                if (credential) {
                    const successRegistration = await this.register(
                        credential as PublicKeyExtendedCredentialRegister,
                    );
                    if (successRegistration) {
                        this.setIsHasUserBiometricRegistration(true);
                        this.setNeedBiometric(true);
                        this.biometricRegistered = true;
                    } else {
                        throw new Error('failed biometry register');
                    }
                } else {
                    throw new Error('failed biometry register');
                }
            } else {
                throw new Error('failed biometry register');
            }
        } catch (e) {
            // eslint-disable-next-line no-console
            console.log(e);
            if (
                isError(e) &&
                e?.message?.match(
                    /operation either timed out or was not allowed/g,
                )
            ) {
                this.internalError = {
                    code: 'userAbortedBiometricCreate',
                    message: 'userAbortedBiometricCreate',
                };
                this.setNeedBiometric(false);
            } else {
                this.setNeedBiometric(true);
            }
            this.setIsHasUserBiometricRegistration(false);

            this.biometricRegistered = false;
            throw new Error('failed biometry register');
        } finally {
            this.isLoadingProcess = false;
        }
    }

    /**
     * @description UserRejectUseBiometric.
     */
    @action.bound
    public userRejectUseBiometric() {
        this.setNeedBiometric(false);
    }

    /**
     * @description UserRejectUseBiometric.
     */
    @action.bound
    public clearData() {
        setNeedBiometric(true);
        this.isLoadingProcess = false;
        this.isCheckIsUserBiometricRegistrationLoadingProcess = false;
        this.isHasUserBiometricRegistration = null;
        this.internalError = null;
        this.biometricRegistered = null;
        this.canBiometricAuth = getCanBiometricAuth();
        this.needBiometric = true;
    }

    /**
     * @description UserRejectUseBiometric.
     */
    @action.bound
    public async userRequestedUseBiometric() {
        if (this.isHasUserBiometricRegistration) {
            this.setNeedBiometric(true);
        } else {
            await this.registerBiometric();
        }
    }

    /**
     * @description HasBiometricRegistration.
     */
    @action.bound
    private async getChallenge() {
        this.internalError = null;
        return BiometricService.getChallenge()
            .then((response) => {
                const { data, result } = response;
                if (result?.code?.match(/00000/) && data?.credentialId) {
                    return {
                        challenge: data?.challenge || '',
                        credentialId: data?.credentialId || '',
                        timeout: data.timeout,
                        userId: data?.credentialId || '',
                    };
                }
                return null;
            })
            .catch(async (err) => {
                if (isServiceResponse(err)) {
                    const data = await err.response.json();
                    this.internalError = {
                        code: data.result.code,
                        message: data.result.message,
                    };
                }
                return null;
            });
    }

    /**
     * @description HasBiometricRegistration.
     */
    @action.bound
    private async initial(): Promise<PublicKeyCredentialCreationOptions | null> {
        return BiometricService.initial()
            .then((response) => {
                const { data, result } = response;
                if (result?.code?.match(/00000/) && data) {
                    return data;
                }
                return null;
            })
            .catch(async (err) => {
                if (isServiceResponse(err)) {
                    const data = await err.response.json();
                    this.internalError = {
                        code: data.result.code,
                        message: data.result.message,
                    };
                }
                return null;
            });
    }

    /**
     * @param {any} credential - Credential.
     * @description HasBiometricRegistration.
     */
    @action.bound
    private async register(credential: PublicKeyExtendedCredentialRegister) {
        // const clientDataJSON = JSON.parse(
        //     new TextDecoder().decode(credential?.response?.clientDataJSON),
        // );
        const formattedData = publicKeyCredentialToJSON(credential);
        const request: CompleteRegisterCredentialRequest = {
            clientDataJSON: formattedData.response.clientDataJSON,
            attestationObject: formattedData.response.attestationObject,
            authenticatorAttachment: credential?.authenticatorAttachment,
        };
        return BiometricService.register(request)
            .then((response) => {
                const { result } = response;
                return !!result?.code?.match(/00000/);
            })
            .catch(async () => false);
    }

    /**
     * @description SetNeedBiometric.
     * @param {boolean} needBiometric - NeedBiometric.
     */
    @action.bound
    private setNeedBiometric(needBiometric: boolean) {
        this.needBiometric = needBiometric;
        setNeedBiometric(needBiometric);
    }

    /**
     * @description InitNeedBiometric.
     */
    @action.bound
    private async InitCanBiometricAuth() {
        if (!localStorage.getItem(LocalStorageKeys.canBiometricAuth)) {
            if (window.PublicKeyCredential) {
                try {
                    const available =
                        await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();

                    if (available) {
                        setCanBiometricAuth(true);
                        this.setNeedBiometric(true);
                    } else {
                        if (process.env.NODE_ENV === 'development') {
                            // eslint-disable-next-line no-console
                            console.log(
                                'WebAuthn supported, Platform Authenticator *not* supported.',
                            );
                        }
                        setCanBiometricAuth(false);
                        this.setNeedBiometric(false);
                    }
                } catch (e) {
                    if (process.env.NODE_ENV === 'development') {
                        // eslint-disable-next-line no-console
                        console.log('Something went wrong.');
                    }

                    setCanBiometricAuth(false);
                    this.setNeedBiometric(false);
                }
            } else {
                setCanBiometricAuth(false);
                this.setNeedBiometric(false);
            }
        }
    }

    /**
     * @description Example of store initialization.
     */
    @action.bound
    private async init(): Promise<void> {
        await this.InitCanBiometricAuth();
        this.canBiometricAuth = getCanBiometricAuth();
        this.needBiometric = getNeedBiometric();
        runInAction(() => {
            this.isInit = true;
        });
    }

    /**
     * @description Info api method.
     * @returns {Promise<InfoMethodResponse>} - Return of info method.
     */
    @action.bound
    private async info(): Promise<InfoMethodResponse | null> {
        return AuthorizationService.info()
            .then(({ data }) => data)
            .catch(async () => null);
    }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isError(err: any): err is Error {
    return err?.message;
}

export { BiometricType };
export const webAuthnStore = new WebAuthnStore();
